import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type MarginReportResult } from '@whinerp/client';
import { formatMoney, formatPercent, todayIn } from '@whinerp/contracts/format';
import { Panel, SectionLabel } from '@whinerp/ui';
import { useFormat, useT } from '../../app/i18n.js';
import { useSession } from '../../app/session.js';

/**
 * Ordered revenue against cost (G-213), only for people who may see cost.
 * Revenue with no recorded cost is its own column, never costed at zero.
 */

const GROUPS = ['product', 'owner', 'month'] as const;
const INPUT = 'h-input rounded-control border border-control bg-surface px-2 text-body text-ink';

export function MarginPanel(): JSX.Element | null {
  const t = useT();
  const format = useFormat();
  const { can } = useSession();
  const enabled = can('sales.margin.view');
  const [by, setBy] = useState<(typeof GROUPS)[number]>('product');
  const to = todayIn(format);
  const from = new Date(Date.parse(to) - 365 * 86_400_000).toISOString().slice(0, 10);
  const report = useQuery<MarginReportResult, ApiError>({
    queryKey: ['margin-report', by, from, to],
    queryFn: () => api.marginReport({ query: { from, to, by } }),
    enabled,
    placeholderData: (previous) => previous,
  });
  if (!enabled || !report.data) return null;
  const money = (amount: string) => formatMoney(amount, format, { currency: report.data.currency });
  return (
    <Panel data-region="margin-report">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <SectionLabel>{t('marginReport.title')}</SectionLabel>
          <select className={`${INPUT} ms-auto`} data-margin-by aria-label={t('marginReport.by')} value={by} onChange={(event) => setBy(event.target.value as 'product')}>
            {GROUPS.map((key) => <option key={key} value={key}>{t(`marginReport.group.${key}`)}</option>)}
          </select>
        </div>
        <p className="text-meta text-ink-meta">{t('marginReport.basis')}</p>
        <div data-scroll-container className="overflow-x-auto">
          <table className="w-full text-dense">
            <thead>
              <tr className="text-meta text-ink-meta">
                <th className="p-1 text-start font-normal">{t(`marginReport.group.${by}`)}</th>
                <th className="p-1 text-end font-normal">{t('marginReport.revenue')}</th>
                <th className="p-1 text-end font-normal">{t('marginReport.cost')}</th>
                <th className="p-1 text-end font-normal">{t('marginReport.margin')}</th>
                <th className="p-1 text-end font-normal">{t('marginReport.marginPct')}</th>
                <th className="p-1 text-end font-normal">{t('marginReport.uncosted')}</th>
              </tr>
            </thead>
            <tbody>
              {report.data.rows.map((row) => (
                <tr key={row.key} data-margin-row={row.key}>
                  <td className="p-1">{row.key}</td>
                  <td className="p-1 text-end">{money(row.revenue)}</td>
                  <td className="p-1 text-end">{money(row.cost)}</td>
                  <td className="p-1 text-end">{money(row.margin)}</td>
                  <td className="p-1 text-end" data-margin-pct>{row.marginPercent === null ? t('marginReport.unknown') : formatPercent(row.marginPercent, format)}</td>
                  <td className="p-1 text-end" data-margin-uncosted>{money(row.uncostedRevenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Panel>
  );
}
